import translate from 'core/translate'
import { cloneDeep } from 'lodash'
import { GroupElement, Rect, TemplateElement } from '../templateEditor.types'
import { nextElementId } from './nextElementId'
import { boundingRectOf } from './selection'
import { rebuildElementsByTopOrder } from './zOrder'

export const getGroupChildren = (elements: TemplateElement[], groupId: string) =>
    elements.filter((element) => element.groupId === groupId)

export const syncGroupBounds = (elements: TemplateElement[], groupId: string): TemplateElement[] => {
    const children = getGroupChildren(elements, groupId)
    const bounds = boundingRectOf(children)
    if (!bounds) {
        return elements.filter((element) => element.id !== groupId)
    }

    return elements.map((element) =>
        element.id === groupId && element.type === 'group' ? { ...element, ...bounds } : element
    )
}

export const syncAllGroupBounds = (elements: TemplateElement[]) => {
    const groupIds = elements.filter((element) => element.type === 'group').map((element) => element.id)
    return groupIds.reduce((acc, groupId) => syncGroupBounds(acc, groupId), elements)
}

/** Разворачивает выбранные id: группа → группа + дети. */
export const getIdsToDelete = (elements: TemplateElement[], selectedIds: string[]) => {
    const ids = new Set<string>()

    for (const id of selectedIds) {
        ids.add(id)
        const element = elements.find((item) => item.id === id)
        if (element?.type === 'group') {
            for (const child of element.children) {
                ids.add(child)
            }
            getGroupChildren(elements, id).forEach((child) => ids.add(child.id))
        }
    }

    return ids
}

export const repairGroups = (elements: TemplateElement[]): TemplateElement[] => {
    const ids = new Set(elements.map((element) => element.id))

    const cleaned = elements
        .map((element) => {
            if (element.groupId && !ids.has(element.groupId)) {
                return { ...element, groupId: null } as TemplateElement
            }
            return element
        })
        .map((element) => {
            if (element.type !== 'group') {
                return element
            }
            const children = element.children.filter((childId) => ids.has(childId))
            return { ...element, children }
        })
        .filter((element) => !(element.type === 'group' && element.children.length === 0))

    return syncAllGroupBounds(cleaned)
}

export const createGroupFromSelection = (
    elements: TemplateElement[],
    selectedIds: string[]
): { elements: TemplateElement[]; groupId: string } | null => {
    const eligible = elements.filter(
        (element) =>
            selectedIds.includes(element.id) &&
            !element.groupId &&
            element.type !== 'group' &&
            !element.locked &&
            !element.hidden
    )

    if (eligible.length < 2) {
        return null
    }

    const bounds = boundingRectOf(eligible)
    if (!bounds) {
        return null
    }

    const groupId = nextElementId('grp')
    const childIds = eligible.map((element) => element.id)
    const group: GroupElement = {
        id: groupId,
        type: 'group',
        name: translate('templateEditorGroup'),
        ...bounds,
        children: childIds,
    }

    const childIdSet = new Set(childIds)
    const next = elements.map((element) => (childIdSet.has(element.id) ? { ...element, groupId } : element))

    const withGroup = [...next, group]
    const topOrder = withGroup.filter((element) => !element.groupId)
    return { elements: rebuildElementsByTopOrder(withGroup, topOrder), groupId }
}

export const ungroupSelection = (
    elements: TemplateElement[],
    selectedIds: string[]
): { elements: TemplateElement[]; childIds: string[] } | null => {
    const group = elements.find(
        (element) => element.type === 'group' && selectedIds.length === 1 && selectedIds[0] === element.id
    ) as GroupElement | undefined

    if (!group) {
        return null
    }

    const childIds = [...group.children]
    const next = elements
        .filter((element) => element.id !== group.id)
        .map((element) => (element.groupId === group.id ? ({ ...element, groupId: null } as TemplateElement) : element))

    return { elements: next, childIds }
}

const idPrefixForType = (type: TemplateElement['type']) => {
    switch (type) {
        case 'text':
            return 'txt'
        case 'shape':
            return 'sh'
        case 'list':
            return 'lst'
        case 'table':
            return 'tbl'
        case 'image':
            return 'img'
        case 'video':
            return 'vid'
        case 'group':
            return 'grp'
        default:
            return 'el'
    }
}

export const duplicateElementTree = (
    elements: TemplateElement[],
    selectedIds: string[],
    offset = 10
): { elements: TemplateElement[]; newIds: string[] } => {
    const reserved = new Set(elements.map((element) => element.id))
    const clones: TemplateElement[] = []
    const newIds: string[] = []
    const idMap = new Map<string, string>()

    const roots = elements.filter((element) => selectedIds.includes(element.id))

    for (const root of roots) {
        if (root.type === 'group') {
            const newGroupId = nextElementId(idPrefixForType('group'), reserved)
            reserved.add(newGroupId)
            idMap.set(root.id, newGroupId)

            const childClones: TemplateElement[] = []
            for (const childId of root.children) {
                const child = elements.find((item) => item.id === childId)
                if (!child) {
                    continue
                }
                const newChildId = nextElementId(idPrefixForType(child.type), reserved)
                reserved.add(newChildId)
                idMap.set(child.id, newChildId)
                childClones.push({
                    ...cloneDeep(child),
                    id: newChildId,
                    x: child.x + offset,
                    y: child.y + offset,
                    groupId: newGroupId,
                })
            }

            const groupClone: GroupElement = {
                ...cloneDeep(root),
                id: newGroupId,
                name: `${root.name} ${translate('templateEditorCopySuffix')}`,
                x: root.x + offset,
                y: root.y + offset,
                children: childClones.map((child) => child.id),
            }

            clones.push(...childClones, groupClone)
            newIds.push(newGroupId)
        } else {
            const newId = nextElementId(idPrefixForType(root.type), reserved)
            reserved.add(newId)
            clones.push({
                ...cloneDeep(root),
                id: newId,
                name: `${root.name} ${translate('templateEditorCopySuffix')}`,
                x: root.x + offset,
                y: root.y + offset,
                groupId: null,
            })
            newIds.push(newId)
        }
    }

    let next = [...elements, ...clones]
    for (const id of newIds) {
        const el = next.find((item) => item.id === id)
        if (el?.type === 'group') {
            next = syncGroupBounds(next, id)
        }
    }

    return { elements: next, newIds }
}

export const scaleElementsInBounds = (
    elements: TemplateElement[],
    ids: string[],
    startBounds: Rect,
    nextBounds: Rect
): TemplateElement[] => {
    if (startBounds.w <= 0 || startBounds.h <= 0) {
        return elements
    }

    const scaleX = nextBounds.w / startBounds.w
    const scaleY = nextBounds.h / startBounds.h
    if (!Number.isFinite(scaleX) || !Number.isFinite(scaleY)) {
        return elements
    }

    const fontScale = Math.min(scaleX, scaleY)
    const idSet = new Set(ids)

    return elements.map((element) => {
        if (!idSet.has(element.id)) {
            return element
        }

        const relX = element.x - startBounds.x
        const relY = element.y - startBounds.y
        const next = {
            ...element,
            x: nextBounds.x + relX * scaleX,
            y: nextBounds.y + relY * scaleY,
            w: Math.max(1, element.w * scaleX),
            h: Math.max(1, element.h * scaleY),
        }

        if (element.type === 'text') {
            return { ...next, fontSize: Math.max(8, element.fontSize * fontScale) }
        }

        return next
    })
}

export const collectMoveIds = (elements: TemplateElement[], selectedIds: string[]) => {
    const ids = new Set<string>()
    for (const id of selectedIds) {
        const element = elements.find((item) => item.id === id)
        if (!element || element.locked) {
            continue
        }
        ids.add(id)
        if (element.type === 'group') {
            getGroupChildren(elements, id).forEach((child) => {
                if (!child.locked) {
                    ids.add(child.id)
                }
            })
        }
    }
    return Array.from(ids)
}
